'use client'

/**
 * ChartHoverLayer — the small hydrated overlay FRIDGE-450 prescribes: the SVG
 * stays server-rendered; this wrapper adds the INSTANT tooltip the card
 * requires (native <title> has a ~300ms browser delay and "does not qualify").
 *
 * Contract with the server chart builders (components/brand-kit/data-figures):
 *   - every data mark carries `data-pt` + `data-l` (label) + `data-v` (value)
 *     and, server-side, `tabindex={0}` + an aria-label — so keyboard and
 *     screen-reader parity exist with ZERO client JS;
 *   - marks whose subject has a live page are wrapped in a real SVG <a>
 *     server-side (native click-through, crawlable, no JS);
 *   - the <svg> root carries `data-chart-interactive`, which is what
 *     scripts/data-interactivity-probe.mjs counts as alive.
 *
 * This component is ONE delegated listener pair + ONE tooltip node for the
 * whole figure — no per-point handlers, no chart library, a few hundred bytes
 * in the page bundle. Budget discipline is the same as FRIDGE-449: the
 * interactivity ships as a sliver, the drawing stays on the server.
 */

import { useCallback, useEffect, useRef } from 'react'

export function ChartHoverLayer({ children }: { children: React.ReactNode }) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const tipRef = useRef<HTMLDivElement>(null)

  const show = useCallback((target: Element) => {
    const wrap = wrapRef.current
    const tip = tipRef.current
    if (!wrap || !tip) return
    const label = target.getAttribute('data-l')
    const value = target.getAttribute('data-v')
    if (!label && !value) return
    tip.textContent = value ? `${label}: ${value}` : label
    const wb = wrap.getBoundingClientRect()
    const tb = target.getBoundingClientRect()
    tip.style.display = 'block'
    // Above the mark, clamped inside the figure.
    const left = Math.min(
      Math.max(tb.left - wb.left + tb.width / 2, 8),
      wb.width - 8
    )
    tip.style.left = `${left}px`
    tip.style.top = `${Math.max(tb.top - wb.top - 8, 0)}px`
  }, [])

  const hide = useCallback(() => {
    const tip = tipRef.current
    if (tip) tip.style.display = 'none'
  }, [])

  /**
   * The listeners are attached IMPERATIVELY rather than as JSX props, and that
   * is deliberate rather than stylistic.
   *
   * This wrapper is not a control — it is a delegation surface. The interactive
   * things are the marks inside it, which carry tabIndex and aria-label
   * server-side and are real SVG <a> elements where their subject has a page.
   * Putting onPointerOver/onFocus on the wrapping <div> made it LOOK like a
   * control to jsx-a11y/no-static-element-interactions, which was correct to
   * flag it: a div with interaction handlers and no role is normally a button
   * someone forgot to build.
   *
   * Suppressing that rule would have been the wrong answer — the honest fix is
   * to stop putting handlers on a non-interactive element at all. Native
   * focusin/focusout are used because, unlike the DOM's focus/blur, they
   * bubble; React's onFocus/onBlur are already delegated focusin/focusout,
   * so behaviour is unchanged. Still exactly one listener pair and one tooltip
   * node for the whole figure.
   */
  useEffect(() => {
    const wrap = wrapRef.current
    if (!wrap) return

    const onOver = (e: Event) => {
      const pt = (e.target as Element | null)?.closest?.('[data-pt]')
      if (pt) show(pt)
    }

    wrap.addEventListener('pointerover', onOver)
    wrap.addEventListener('pointerout', hide)
    wrap.addEventListener('focusin', onOver)
    wrap.addEventListener('focusout', hide)
    return () => {
      wrap.removeEventListener('pointerover', onOver)
      wrap.removeEventListener('pointerout', hide)
      wrap.removeEventListener('focusin', onOver)
      wrap.removeEventListener('focusout', hide)
    }
  }, [show, hide])

  return (
    <div ref={wrapRef} className="relative">
      {children}
      <div
        ref={tipRef}
        role="status"
        aria-live="polite"
        className="pointer-events-none absolute z-10 hidden -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-[12px] font-semibold text-gray-900 shadow-md"
        style={{ display: 'none' }}
      />
    </div>
  )
}
